/**
 * Analytics - Camada de abstração para Posthog
 *
 * Componentes NUNCA importam posthog-js diretamente.
 * Em dev sem key, tudo é no-op com console.log.
 */

import posthog from 'posthog-js';

const isProd = import.meta.env.VITE_ENV === 'production';
const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

/**
 * Inicializa o Posthog SDK (chamado uma vez no main.tsx)
 */
export function initAnalytics(): void {
  if (!posthogKey) {
    console.log('[Analytics] Sem VITE_POSTHOG_KEY, analytics desabilitado');
    return;
  }

  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    autocapture: true,
    disable_session_recording: !isProd,
    respect_dnt: true,
    capture_performance: true,
    capture_pageview: false, // usamos hook manual com React Router
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      if (!isProd) ph.debug();
    },
  });
}

/**
 * Identifica o usuário após login (vincula anônimo → real)
 */
export function identifyUser(user: {
  id: string;
  email?: string;
  name?: string;
  level?: number;
  xp?: number;
  masterkey?: string;
  role?: string;
}): void {
  if (!posthogKey) return;

  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    level: user.level,
    xp: user.xp,
    masterkey: user.masterkey,
    role: user.role,
  });

  if (!isProd) console.log('[Analytics] identify:', user.id);
}

/**
 * Reset ao fazer logout
 */
export function resetUser(): void {
  if (!posthogKey) return;
  posthog.reset();
}

/**
 * Rastreia mudança de página (React Router)
 */
export function trackPageView(path: string): void {
  if (!posthogKey) return;

  posthog.capture('$pageview', {
    $current_url: window.location.href,
    path,
  });

  if (!isProd) console.log('[Analytics] pageview:', path);
}

// ============================================================================
// EVENTOS CUSTOMIZADOS
// ============================================================================

type AnalyticsEvent =
  | 'login_success'
  | 'login_failed'
  | 'story_created'
  | 'story_analyzed'
  | 'dna_extraction_started'
  | 'dna_extraction_completed'
  | 'dna_extraction_failed'
  | 'pricing_viewed'
  | 'plan_selected'
  | 'checkout_started'
  | 'game_played'
  | 'invite_generated'
  | 'invite_redeemed'
  | 'diario_opened'
  | 'bussola_completed'
  | 'ficha_generated';

function track(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
  if (!posthogKey) return;
  posthog.capture(event, properties);
  if (!isProd) console.log(`[Analytics] ${event}:`, properties);
}

// ============================================================================
// API PÚBLICA - Funções nomeadas com props tipadas
// ============================================================================

export const analytics = {
  // Auth
  trackLoginSuccess: (props: { method: 'masterkey' | 'google' | 'discord' }) =>
    track('login_success', props),

  trackLoginFailed: (props: { method: string; error: string }) =>
    track('login_failed', props),

  // Diário de Bordo
  trackStoryCreated: (props: { wordCount: number; hasTrigger: boolean; triggerId?: string }) =>
    track('story_created', props),

  trackStoryAnalyzed: (props: { storyId: string }) =>
    track('story_analyzed', props),

  // DNA Extraction (3 tipos)
  trackDNAExtractionStarted: (props: { type: 'criativo' | 'comportamental' | 'imersivo'; keySelection: string }) =>
    track('dna_extraction_started', props),

  trackDNAExtractionCompleted: (props: { type: 'criativo' | 'comportamental' | 'imersivo'; dnaId?: string; durationMs?: number }) =>
    track('dna_extraction_completed', props),

  trackDNAExtractionFailed: (props: { type: 'criativo' | 'comportamental' | 'imersivo'; error: string; failedLayer?: string }) =>
    track('dna_extraction_failed', props),

  // Pricing / Checkout
  trackPricingViewed: () =>
    track('pricing_viewed'),

  trackPlanSelected: (props: { planId: string; planName: string; billingPeriod: string }) =>
    track('plan_selected', props),

  trackCheckoutStarted: (props: { planCode: string; billingPeriod: string }) =>
    track('checkout_started', props),

  // Fliperama
  trackGamePlayed: (props: { gameId: string; gameTitle: string }) =>
    track('game_played', props),

  // Invites
  trackInviteGenerated: () =>
    track('invite_generated'),

  trackInviteRedeemed: (props: { inviteCode: string; method: string }) =>
    track('invite_redeemed', props),

  // Engagement
  trackDiarioOpened: () =>
    track('diario_opened'),

  trackBussolaCompleted: () =>
    track('bussola_completed'),

  trackFichaGenerated: () =>
    track('ficha_generated'),
} as const;
